import React from "react";
import styled from "styled-components";
import { FaExclamationTriangle } from "react-icons/fa";

interface WarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  actionText: string;
  onAction: () => void;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalWrapper = styled.div`
  background-color: #fff;
  border-radius: 12px;
  padding: 0;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background-color: #fff9c4;
  border-bottom: 3px solid #f0b90b;
`;

const IconWrapper = styled.div`
  color: #c99700;
  font-size: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainHeading = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: #b8860b;
  letter-spacing: 0.3px;
`;

const Title = styled.h2`
  font-size: 20px;
  margin: 0;
  font-weight: 600;
  color: #333;
`;

const Message = styled.p`
  margin: 0;
  padding: 20px;
  color: #444;
  font-size: 15px;
  line-height: 1.6;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  background-color: #fafafa;
  border-top: 1px solid #e0e0e0;
  gap: 10px;
`;

const ActionButton = styled.button`
  background-color: #f0b90b;
  color: #333;
  padding: 9px 24px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #e0a800;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const CancelButton = styled.button`
  background-color: #fff;
  color: #666;
  padding: 9px 24px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    border-color: #999;
    color: #333;
  }

  &:active {
    background-color: #e0e0e0;
  }
`;

const WarningModal: React.FC<WarningModalProps> = ({
  isOpen,
  onClose,
  title = "Warning",
  message,
  actionText,
  onAction,
}) => {
  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalWrapper onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        <Banner>
          <IconWrapper>
            <FaExclamationTriangle />
          </IconWrapper>
          <HeadingGroup>
            <MainHeading>Warning</MainHeading>
          </HeadingGroup>
        </Banner>
        <Message>{message}</Message>
        <ButtonWrapper>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <ActionButton
            onClick={() => {
              onAction();
              onClose();
            }}
          >
            {actionText}
          </ActionButton>
        </ButtonWrapper>
      </ModalWrapper>
    </Overlay>
  );
};

export default WarningModal;
